import { useCallback, useState } from 'react';
import { Form, message } from 'antd';
import { openFormDecision } from './formDecisionDialog';
import { runAiAction } from '../../services/aiActionApi';
import { applyScalarFieldValues, buildAddRowInitial } from './applyGroupValues';

function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = String(html ?? '');
  return div.textContent || div.innerText || '';
}

function applyTransform(value, transform) {
  if (!transform) return value;
  if (transform === 'stripHtml') return stripHtml(value);
  return value;
}

function wordCount(text) {
  return String(text ?? '').trim().split(/\s+/).filter(Boolean).length;
}

function rowHasData(row) {
  return Object.values(row || {}).some((v) => v !== undefined && v !== null && v !== '');
}

// isInputSatisfied — an input "counts" once it has real content: a file
// present for kind "file", or non-empty (post-transform) text for kind
// "text" that also meets the input's own MinWords, if configured.
function isInputSatisfied(input, rawValue) {
  if (input.kind === 'file') {
    return Array.isArray(rawValue) ? rawValue.length > 0 : Boolean(rawValue);
  }
  const text = applyTransform(rawValue, input.transform);
  const str = String(text ?? '').trim();
  if (!str) return false;
  const minWords = Number(input.minWords) || 0;
  return minWords > 0 ? wordCount(str) >= minWords : true;
}

// computeActionEnabled — an action is disabled until every GATED input is
// satisfied. An input gates the button when it's marked Required, or has a
// MinWords requirement (>0) — a plain optional input never gates. Inputs
// sharing a Group (e.g. "either a JD file or JD text") gate as one unit: the
// group is satisfied once ANY member of it is satisfied.
function computeActionEnabled(action, getValue) {
  const inputs = action.inputs || [];
  if (inputs.length === 0) return true;

  const groupsMap = new Map();
  inputs.forEach((input, i) => {
    const key = input.group || `__solo_${i}`;
    if (!groupsMap.has(key)) groupsMap.set(key, []);
    groupsMap.get(key).push(input);
  });

  for (const groupInputs of groupsMap.values()) {
    const gates = groupInputs.some((i) => i.required || Number(i.minWords) > 0);
    if (!gates) continue;
    const satisfied = groupInputs.some((i) => isInputSatisfied(i, getValue(i.sourceField)));
    if (!satisfied) return false;
  }
  return true;
}

// targetFieldsOf — every FORM FIELD an action's response would write into.
// Derived from the response itself (the {groups} shape the parser returns), so
// it needs no per-action configuration and stays correct as an action's output
// changes.
export function targetFieldsOf(responseGroups = []) {
  const keys = [];
  responseGroups.forEach((group) => {
    if (group?.addRow) {
      if (group.name) keys.push(group.name);
      return;
    }
    (group?.fields ?? []).forEach((f) => {
      const key = f?.field ?? f?.name;
      if (key) keys.push(key);
    });
  });
  return keys;
}

// hasExistingData — would applying this response OVERWRITE something the user
// already typed? Used to decide whether to ask first.
export function hasExistingData(form, responseGroups) {
  return targetFieldsOf(responseGroups).some((key) => {
    const value = form.getFieldValue(key);
    if (Array.isArray(value)) return value.some(rowHasData);
    return value !== undefined && value !== null && value !== '';
  });
}

/**
 * formHasUserData — has anyone typed anything into this form yet, ignoring the
 * field that triggered the action?
 *
 * Used for UPLOAD-triggered actions, where the question has to be answered
 * BEFORE the file is sent anywhere. At that point the response does not exist,
 * so the precise "would this overwrite a target field?" test cannot be run —
 * but "the form is still blank" is knowable, and it is the case that matters:
 * an empty form can be filled in silently, a form someone has worked on cannot.
 */
/**
 * countUserEntries — how much the user has actually filled in, ignoring the
 * field that triggered the action.
 *
 * The overwrite prompt needs to state what is AT RISK. "Already has details
 * entered" is not a fact — it is a restatement of why the dialog opened, which
 * tells the reader nothing they did not already know. A count does: it is the
 * difference between "I typed one thing by accident" and "I have filled in half
 * this form".
 */
export function countUserEntries(form, excludeFields = []) {
  const values = form.getFieldsValue(true) ?? {};
  const skip = new Set([excludeFields].flat().filter(Boolean));
  const touched = typeof form.isFieldTouched === 'function'
    ? (key) => form.isFieldTouched(key)
    : () => true;

  let count = 0;
  Object.entries(values).forEach(([key, value]) => {
    // Counted on the SAME basis the prompt is shown on. Counting untouched
    // defaults here would say "you have filled in 4 answers" to someone who has
    // filled in one.
    if (skip.has(key) || !touched(key) || !hasRealValue(value)) return;
    if (Array.isArray(value)) {
      count += value.filter((row) => (row && typeof row === 'object' ? rowHasData(row) : Boolean(row))).length;
      return;
    }
    count += 1;
  });
  return count;
}

// hasRealValue — is there something here the user would mind losing?
function hasRealValue(value) {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) {
    // A Form.List that mounted with one blank row is not "user data".
    return value.some((row) => (row && typeof row === 'object' ? rowHasData(row) : Boolean(row)));
  }
  if (typeof value === 'object') {
    return Object.values(value).some((v) => v !== undefined && v !== null && v !== '');
  }
  return true;
}

export function formHasUserData(form, excludeFields = []) {
  const values = form.getFieldsValue(true) ?? {};
  const skip = new Set([excludeFields].flat().filter(Boolean));
  const keys = Object.keys(values).filter((key) => !skip.has(key));

  // TOUCHED, not merely non-empty.
  //
  // A brand-new candidate form is NOT blank: four fields already carry values
  // from their configured defaults (VMS commission 5.5, VMS type "Recurring",
  // rate currency, rate unit). Judging by emptiness alone therefore reported
  // "the user has filled things in" on a form nobody had typed into, so the
  // very first résumé upload — the one that should just work — stopped to ask
  // permission to overwrite defaults the user had never seen.
  //
  // antd sets `touched` on USER interaction only: neither Form.Item
  // initialValue nor a programmatic setFieldsValue marks a field touched, which
  // is exactly the distinction needed. A field still has to hold something too,
  // so typing into a box and then clearing it does not count.
  if (typeof form.isFieldTouched === 'function') {
    return keys.some((key) => form.isFieldTouched(key) && hasRealValue(values[key]));
  }

  // No touch tracking available (a bare form object): fall back to emptiness.
  return keys.some((key) => hasRealValue(values[key]));
}

/**
 * parsedPayload — the scalar values a response carries, as a flat record.
 *
 * The duplicate check needs an email and a phone number, and after a résumé is
 * read those exist in the RESPONSE, not yet in the form. Checking the form here
 * would ask "is this person already on file?" about the blank page the user is
 * still looking at.
 *
 * Repeatable groups are skipped: nothing identifies a person by their third job.
 */
export function parsedPayload(responseGroups = []) {
  const out = {};
  (responseGroups ?? []).forEach((group) => {
    if (group?.addRow) return;
    (group?.fields ?? []).forEach((f) => {
      const key = f?.field ?? f?.name;
      if (!key) return;
      const value = f?.value;
      if (value === undefined || value === null || value === '') return;
      out[key] = value;
    });
  });
  return out;
}

// shouldConfirmApply — the admin-configured overwrite policy for an action.
//
//   'targetsFilled' (default for upload-triggered parses) — ask only when the
//                   user has already filled something the parse would replace.
//                   An upload BEFORE typing stays silent (the fast path); an
//                   upload AFTER typing always asks, which is exactly the
//                   requirement.
//   'always'      — ask every time.
//   'never'       — apply silently (the behaviour before this existed).
export function shouldConfirmApply(action, form, responseGroups) {
  const mode = action?.confirmWhen ?? 'never';
  if (mode === 'never') return false;
  if (mode === 'always') return true;
  return hasExistingData(form, responseGroups);
}

const AI_ACTION_POSITIONS = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'];
const DEFAULT_AI_ACTION_POSITION = 'bottom-left';

// groupActionsByPosition — buckets a field's click-triggered AI actions by
// their admin-configured Position (blank/unknown falls back to the original
// "bottom-left" layout), so the caller can render each bucket in its own
// slot around the field with the right alignment.
export function groupActionsByPosition(actions = []) {
  const buckets = {};
  actions.forEach((action) => {
    const pos = AI_ACTION_POSITIONS.includes(action.position) ? action.position : DEFAULT_AI_ACTION_POSITION;
    (buckets[pos] ??= []).push(action);
  });
  return buckets;
}

export { AI_ACTION_POSITIONS };

/**
 * useAiActions — shared logic behind config-driven "AI Action" buttons
 * (Generate/Parse-style: send some form fields to an admin-configured
 * service, get back the SAME `{groups: [...]}` shape getFormGroups returns
 * for Edit prefill, and apply it to the form via the same shared functions).
 * Used identically by AddFormV1 and EditFormV1 so the apply logic isn't
 * duplicated.
 *
 * @param {Object} args
 * @param {import('antd').FormInstance} args.form
 * @param {Array} args.groups - the module's form groups (for input lookups)
 * @param {string} args.module
 */
export default function useAiActions({ form, groups, module, onApplied, onParsed }) {
  const [loadingKey, setLoadingKey] = useState(null);
  // What the spinner SAYS. A bare "Loading…" over a form that has just been
  // taken away from the user tells them nothing about how long to wait or why.
  // Admin-configured per action (action.loadingText); the fallback still names
  // the action rather than the mechanism.
  const [loadingText, setLoadingText] = useState('');
  // Re-renders whenever ANY field changes, so isActionEnabled below reflects
  // live typing/upload without needing to know in advance which field names
  // any given action reads from (fully config-driven, no hardcoded paths).
  const watchedValues = Form.useWatch((values) => values, form);

  const isActionEnabled = useCallback(
    (action) => computeActionEnabled(action, (sourceField) => (watchedValues || {})[sourceField]),
    [watchedValues],
  );

  // Returns whether anything was actually applied to the form, so the caller
  // can show an accurate success vs. "nothing found" message — generic to any
  // action/module, since it only looks at what the response itself contained.
  const applyResponse = useCallback((responseGroups) => {
    if (!Array.isArray(responseGroups) || responseGroups.length === 0) return false;

    const scalarValues = applyScalarFieldValues(form, responseGroups.filter((g) => !g.addRow));
    let appliedAny = Object.keys(scalarValues).length > 0;

    responseGroups.forEach((group) => {
      if (!group.addRow) return;
      if (!Array.isArray(group.rows) || group.rows.length === 0) return;

      const shapedRows = buildAddRowInitial(group);
      if (shapedRows.length === 0) return;
      appliedAny = true;

      // A mounted Form.List's field name is already registered in the Form's
      // store (even with an empty array) — setFieldsValue is what antd
      // documents for a post-mount update; it re-renders the List with the
      // new rows directly, no remount trick needed.
      const applyRows = () => form.setFieldsValue({ [group.name]: shapedRows });

      const existingRows = form.getFieldValue(group.name) || [];
      const filled = existingRows.filter(rowHasData);
      if (filled.length) {
        const sectionName = group.label || group.name;
        openFormDecision({
          tone: 'overwrite',
          title: `Replace what's in ${sectionName}?`,
          facts: [
            { label: 'Section', value: sectionName },
            { label: 'Rows you filled in', value: filled.length },
            { label: 'Rows in the file', value: shapedRows.length },
          ],
          body: `Everything currently in ${sectionName} will be removed and replaced with what the file says. `
            + 'The rest of the form is not affected.',
          okText: 'Replace them',
          cancelText: 'Keep mine',
          danger: true,
        }).then((confirmed) => { if (confirmed) applyRows(); });
      } else {
        applyRows();
      }
    });

    return appliedAny;
  }, [form]);

  const runAction = useCallback(async (groupName, field, action, extraFile) => {
    // ── ask BEFORE sending the file anywhere ────────────────────────────────
    // For an upload action the question is "shall I read this and fill the
    // form in?", and it has to be asked before the call, not after: calling
    // first wastes a round-trip on a file the user may not want parsed, and it
    // sends their document to a service for nothing.
    const mode = action?.confirmWhen ?? 'never';
    if (action?.trigger === 'upload' && mode !== 'never') {
      const dirty = mode === 'always' || formHasUserData(form, [field?.field]);
      if (dirty) {
        const msgs = action.confirmMessages ?? {};
        // Naming the FILE matters: by this point the user has picked something,
        // and "this file" only reassures if they can see it is the one they
        // meant. The count says what is at risk — see countUserEntries.
        const fileName = extraFile?.name
          || form.getFieldValue(field?.field)?.slice?.(-1)?.[0]?.name;
        const entries = countUserEntries(form, [field?.field]);
        const proceed = await openFormDecision({
          tone: 'overwrite',
          title: msgs.title || 'Read this file and fill the form in?',
          facts: [
            { label: 'File', value: fileName },
            {
              label: 'You have filled in',
              value: entries ? `${entries} ${entries === 1 ? 'answer' : 'answers'}` : undefined,
            },
          ],
          // Present/future tense: nothing has been read yet. The confirm now
          // runs BEFORE the file is sent anywhere, so past-tense copy ("we read
          // the details…") describes something that has not happened.
          body: msgs.body
            || 'We can read the details out of this file and fill the form in for you. '
               + 'Where the file has an answer, it replaces what is currently in that box. '
               + 'Anything the file does not mention is left as you typed it.',
          okText: msgs.ok || 'Read it and fill in',
          cancelText: msgs.cancel || 'Keep what I typed',
        });
        if (!proceed) return undefined;
      }
    }

    setLoadingKey(action.key);
    setLoadingText(action.loadingText || `Reading ${action.label || 'the file'}…`);
    try {
      const inputs = {};
      const files = {};

      (action.inputs || []).forEach((input) => {
        if (input.kind === 'file') {
          const fromFileList = form.getFieldValue(input.sourceField);
          const file = extraFile ?? fromFileList?.[fromFileList.length - 1]?.originFileObj;
          if (file) files[input.param] = file;
        } else {
          const raw = form.getFieldValue(input.sourceField);
          if (raw !== undefined && raw !== null && raw !== '') {
            inputs[input.param] = applyTransform(raw, input.transform);
          }
        }
      });
      // Within a shared input Group (e.g. "either a JD file or JD text"), a
      // file input wins — drop the sibling text input if both are present.
      (action.inputs || []).forEach((input) => {
        if (!input.group || input.kind !== 'text') return;
        const fileSiblingProvided = (action.inputs || [])
          .some((i) => i.group === input.group && i.kind === 'file' && files[i.param]);
        if (fileSiblingProvided) delete inputs[input.param];
      });

      const result = await runAiAction({
        module, group: groupName, field: field.field, actionKey: action.key, inputs, files,
      });
      const label = action.label || 'AI action';
      const responseGroups = result?.groups;

      // Click actions still ask AFTER the call, because only the response says
      // which fields they would touch. Upload actions have already asked above.
      if (action?.trigger !== 'upload' && shouldConfirmApply(action, form, responseGroups)) {
        const msgs = action.confirmMessages ?? {};
        // A click action knows exactly which boxes it would overwrite, because
        // the response has already come back — so it can say so.
        const targets = targetFieldsOf(responseGroups).length;
        const confirmed = await openFormDecision({
          tone: 'overwrite',
          title: msgs.title || `Fill the form in from ${label}?`,
          facts: [
            { label: 'Source', value: label },
            { label: 'Boxes it would fill', value: targets || undefined },
          ],
          body: msgs.body
            || 'We found details you can use. Applying them will replace what you have '
               + 'already entered in those boxes.',
          okText: msgs.ok || 'Use these details',
          cancelText: msgs.cancel || 'Keep what I typed',
        });
        if (!confirmed) {
          message.info(`${label} cancelled — your entries were kept.`);
          return result;
        }
      }

      // ── between reading and applying ──────────────────────────────────
      // The host gets to veto BEFORE any value lands on the form. For a résumé
      // this is where "do we already have this person?" is asked: the parsed
      // email and phone exist now, and if the answer is yes there is no point
      // filling in a form the user is about to abandon.
      //
      // A veto returns the result unapplied — the host has already told the
      // user why and decided what to do with the file.
      if (onParsed) {
        const verdict = await onParsed({
          action,
          field,
          groupName,
          responseGroups,
          payload: parsedPayload(responseGroups),
        });
        if (verdict === false || verdict === 'abort') return result;
      }

      const appliedAny = applyResponse(responseGroups);
      if (appliedAny) {
        message.success(`${label} completed successfully.`);
      } else {
        message.info(`${label} completed, but no matching details were found.`);
      }
      // After the form is filled — a second, cheaper check that also covers
      // anything the user had already typed.
      if (appliedAny && onApplied) await onApplied({ action, field, groupName });
      return result;
    } catch (error) {
      message.error(error?.message || `${action.label || 'AI action'} failed`);
      return undefined;
    } finally {
      setLoadingKey(null);
      setLoadingText('');
    }
  }, [form, module, applyResponse, onApplied, onParsed]);

  return { runAction, loadingKey, loadingText, busy: loadingKey !== null, isActionEnabled };
}
